import { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useMembers from './useMembers';
import './admin.css';

const ROLE_LABEL = {
  owner: '소유자',
  admin: '관리자',
  member: '멤버',
  restricted: '제한 멤버',
  guest: '게스트',
};
const STATUS_LABEL = { active: '활성', invited: '초대됨', disabled: '비활성' };

// 멤버 역할 변경 시 선택 가능한 값 (owner 는 시드/이관으로만 부여 → 드롭다운 제외)
const ASSIGNABLE_ROLES = ['admin', 'member', 'restricted', 'guest'];
// 초대 시 부여 가능한 역할 (DB invitations check 제약: admin/member/restricted/guest)
const INVITE_ROLES = ['admin', 'member', 'restricted', 'guest'];

// 이름 → 안정적인 아바타 색상
const AV_COLORS = ['#7c3aed', '#16a34a', '#d97706', '#2383e2', '#db2777', '#0891b2', '#65a30d'];
function avatarColor(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}
function initial(name) {
  const t = String(name || '').trim();
  return t ? t[0].toUpperCase() : '·';
}

function Avatar({ name, url, className }) {
  if (url) {
    return (
      <span
        className={`adm-av ${className || ''}`}
        style={{ backgroundImage: `url(${url})` }}
        aria-hidden
      />
    );
  }
  return (
    <span className={`adm-av ${className || ''}`} style={{ background: avatarColor(name) }}>
      {initial(name)}
    </span>
  );
}

export default function MembersPage() {
  const { user, membership } = useAuth();
  const isAdmin = membership && (membership.role === 'owner' || membership.role === 'admin');

  const { members, invites, loading, error, updateRole, createInvite, revokeInvite } = useMembers();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { type:'ok'|'err', text }
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const counts = useMemo(() => {
    const guests = members.filter((m) => m.role === 'guest').length;
    return { total: members.length, guests, pending: invites.length };
  }, [members, invites]);

  if (!isAdmin) {
    return (
      <div className="adm-wrap">
        <div className="adm-deny">
          <div className="ic">🔒</div>
          <h3>권한 없음</h3>
          <p>멤버·초대 관리는 소유자 또는 관리자만 사용할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  async function onInvite() {
    setNotice(null);
    setCreatedLink(null);
    setCopied(false);
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteBusy(true);
    const res = await createInvite({ email, role: inviteRole });
    setInviteBusy(false);
    if (!res.ok) {
      setNotice({ type: 'err', text: '초대 생성 실패: ' + res.error });
      return;
    }
    const link = `${window.location.origin}/invite?token=${res.token}`;
    setInviteEmail('');
    setCreatedLink({ link, email: res.email });
    setNotice({ type: 'ok', text: `${res.email} 초대를 생성했습니다.` });
  }

  async function copyLink() {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setNotice({ type: 'err', text: '클립보드 복사에 실패했습니다. 링크를 직접 선택해 복사하세요.' });
    }
  }

  async function onChangeRole(member, newRole) {
    if (newRole === member.role) return;
    setNotice(null);
    const res = await updateRole(member.id, newRole);
    if (!res.ok) {
      setNotice({ type: 'err', text: '역할 변경 실패: ' + res.error });
    }
  }

  async function onRevoke(inv) {
    setNotice(null);
    const res = await revokeInvite(inv.id);
    if (!res.ok) {
      setNotice({ type: 'err', text: '초대 취소 실패: ' + res.error });
    }
  }

  return (
    <div className="adm-wrap">
      <div className="adm-head">
        <div className="adm-title">멤버 · 초대</div>
        <div className="adm-sub">
          멤버 {counts.total}명 · 대기 중 초대 {counts.pending}건
          {counts.guests > 0 && ` · 게스트 ${counts.guests}명`}
        </div>
      </div>

      {error && <div className="adm-note err">데이터 로드 오류: {error}</div>}
      {notice && <div className={`adm-note ${notice.type}`}>{notice.text}</div>}

      {/* 초대 영역 */}
      <div className="adm-invite">
        <input
          type="email"
          placeholder="초대할 이메일 입력 (name@rhoonart.com)"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onInvite(); }}
        />
        <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} aria-label="초대 역할">
          {INVITE_ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </select>
        <button className="adm-btn" onClick={onInvite} disabled={inviteBusy || !inviteEmail.trim()}>
          {inviteBusy ? '생성 중…' : '초대 링크 생성'}
        </button>
      </div>

      {createdLink && (
        <div className="adm-link-box">
          <code title={createdLink.link}>{createdLink.link}</code>
          <button className="adm-btn ghost sm" onClick={copyLink}>
            {copied ? '복사됨 ✓' : '🔗 링크 복사'}
          </button>
        </div>
      )}

      {/* 멤버 목록 */}
      <div className="adm-card" style={{ marginTop: 16 }}>
        <div className="adm-card-h"><span className="lbl">멤버</span></div>
        {loading ? (
          <div className="adm-loading"><span className="spinner" /> 불러오는 중…</div>
        ) : members.length === 0 ? (
          <div className="adm-empty">멤버가 없습니다.</div>
        ) : (
          members.map((m) => {
            const isSelf = m.user_id === user?.id;
            const lockRole = m.role === 'owner' || isSelf; // owner 행·본인 행은 변경 잠금
            return (
              <div className="adm-row" key={m.id}>
                <Avatar name={m.display_name} url={m.avatar_url} />
                <div className="who">
                  <div className="nm">
                    {m.display_name || '(이름 없음)'}
                    {isSelf && <span className="muted" style={{ fontWeight: 400 }}> · 나</span>}
                  </div>
                  <div className="em">
                    <span className={`adm-status ${m.status}`}>
                      <span className="dot" />{STATUS_LABEL[m.status] || m.status}
                    </span>
                  </div>
                </div>
                {lockRole ? (
                  <span className={`adm-tag ${m.role}`}>{ROLE_LABEL[m.role] || m.role}</span>
                ) : (
                  <select
                    className="adm-role"
                    value={m.role}
                    onChange={(e) => onChangeRole(m, e.target.value)}
                    aria-label={`${m.display_name} 역할`}
                  >
                    {/* 현재 값이 assignable 목록 밖이면(예: 과거 데이터) 별도 표시 */}
                    {!ASSIGNABLE_ROLES.includes(m.role) && (
                      <option value={m.role}>{ROLE_LABEL[m.role] || m.role}</option>
                    )}
                    {ASSIGNABLE_ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 대기 중 초대 */}
      <div className="adm-card">
        <div className="adm-card-h"><span className="lbl">대기 중 초대</span></div>
        {loading ? (
          <div className="adm-loading"><span className="spinner" /> 불러오는 중…</div>
        ) : invites.length === 0 ? (
          <div className="adm-empty">대기 중인 초대가 없습니다.</div>
        ) : (
          invites.map((inv) => (
            <div className="adm-row" key={inv.id} style={{ opacity: 0.92 }}>
              <Avatar name={inv.email} className="invite" />
              <div className="who">
                <div className="nm" style={{ color: 'var(--text-2)' }}>{inv.email}</div>
                <div className="em">
                  역할 {ROLE_LABEL[inv.role] || inv.role}
                  {inv.expires_at && ` · 만료 ${new Date(inv.expires_at).toLocaleDateString('ko-KR')}`}
                </div>
              </div>
              <span className="adm-tag pending">대기 중</span>
              <button className="adm-btn danger sm" onClick={() => onRevoke(inv)}>취소</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
